/**
 * CommandCentreBase — Reusable auto-cycling scenario showcase.
 *
 * Shared engine for both Vortex and Cortex Command Centre pages.
 * Receives product-specific scenarios, theming, and content via props.
 * Auto-cycles through scenarios with typed commands and animated output.
 * Pauses on hover/touch for readability.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────

export interface OutputLine {
  text: string;
  type: 'header' | 'info' | 'success' | 'warning' | 'error' | 'dimmed' | 'accent' | 'data';
}

export interface Highlight {
  label: string;
  value: string;
  color: 'cyan' | 'green' | 'yellow' | 'red';
}

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  command?: string;
  outputLines: OutputLine[];
  highlight?: Highlight;
}

export interface CommandCentreConfig {
  scenarios: Scenario[];
  cycleDurationMs: number;
  productName: string;           // "Vortex" or "Cortex"
  productLabel: string;          // "Command Centre" or "Intelligence Hub"
  heroTitle: string;
  heroSubtitle: string;
  backLabel?: string;            // "← Kempion" default
  backPath?: string;             // "/" default
  terminalLabel?: string;        // Text in terminal title bar
}

// ── Line Styles ──────────────────────────────────────────

const lineStyles: Record<OutputLine['type'], string> = {
  header: 'text-white font-semibold',
  info: 'text-gray-300',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
  dimmed: 'text-gray-600',
  accent: 'text-accent',
  data: 'text-cyan-300',
};

// ── Typed Command ────────────────────────────────────────

const TypedCommand = ({ text }: { text: string }) => {
  // Handle special non-command entries (auto-triggered, proactive)
  if (text.startsWith('[')) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        className="text-gray-500 italic text-xs"
      >
        {text}
      </motion.div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-accent select-none">$</span>
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap font-mono text-white"
        initial={{ width: 0 }}
        animate={{ width: `${text.length}ch` }}
        transition={{ duration: text.length * 0.035, ease: 'linear', delay: 0.2 }}
      >
        {text}
      </motion.span>
      <motion.span
        className="inline-block w-[1ch] h-[1.2em] bg-accent/60"
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
      />
    </div>
  );
};

// ── Animated Line ────────────────────────────────────────

const AnimatedLine = ({ line, index, commandLength }: { line: OutputLine; index: number; commandLength: number }) => {
  const typeDelay = commandLength * 0.035 + 0.2;
  const lineDelay = typeDelay + 0.15 + index * 0.15;

  if (line.text === '') {
    return <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: lineDelay }} className="h-2.5" />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: lineDelay, duration: 0.25 }}
      className={`text-sm leading-relaxed whitespace-pre ${lineStyles[line.type]}`}
    >
      {line.text}
    </motion.div>
  );
};

// ── Highlight Card ───────────────────────────────────────

const HighlightCard = ({ highlight, delay }: { highlight: Scenario['highlight']; delay: number }) => {
  if (!highlight) return null;

  const borderColors = {
    cyan: 'border-accent/30 bg-accent/5',
    green: 'border-green-500/30 bg-green-500/5',
    yellow: 'border-yellow-500/30 bg-yellow-500/5',
    red: 'border-red-500/30 bg-red-500/5',
  };
  const textColors = {
    cyan: 'text-accent',
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`mt-4 p-3 rounded-lg border ${borderColors[highlight.color]}`}
    >
      <span className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">{highlight.label}</span>
      <div className={`text-sm font-mono mt-1 ${textColors[highlight.color]}`}>{highlight.value}</div>
    </motion.div>
  );
};

// ── Scenario Panel ───────────────────────────────────────

const ScenarioPanel = ({ scenario }: { scenario: Scenario }) => {
  const cmdLen = scenario.command?.length ?? 0;
  const totalLineDelay = cmdLen * 0.035 + 0.2 + 0.15 + scenario.outputLines.length * 0.15 + 0.2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35 }}
      className="w-full max-w-2xl mx-auto"
    >
      {/* Scenario header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.05 }}
        className="flex items-center gap-3 mb-5"
      >
        <div className="w-10 h-10 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center">
          <div className="w-2.5 h-2.5 rounded-full bg-accent" />
        </div>
        <div>
          <h3 className="text-lg font-light text-white">{scenario.title}</h3>
          <p className="text-xs text-gray-500">{scenario.subtitle}</p>
        </div>
        <span className="ml-auto px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider text-gray-600 border border-gray-800">
          {scenario.category}
        </span>
      </motion.div>

      {/* Terminal panel */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/40" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/40" />
          <span className="ml-3 text-[10px] font-mono text-gray-600 uppercase tracking-widest">
            {scenario.category.toLowerCase()}
          </span>
        </div>

        {/* Terminal content */}
        <div className="p-5 font-mono text-sm space-y-0.5 min-h-[300px]">
          {scenario.command && <TypedCommand text={scenario.command} />}

          <div className="mt-3 space-y-0.5">
            {scenario.outputLines.map((line, i) => (
              <AnimatedLine key={`${scenario.id}-${i}`} line={line} index={i} commandLength={cmdLen} />
            ))}
          </div>

          <HighlightCard highlight={scenario.highlight} delay={totalLineDelay} />
        </div>
      </div>
    </motion.div>
  );
};

// ── Progress Dots ────────────────────────────────────────

const ProgressDots = ({
  count,
  active,
  onSelect,
  isPaused,
  cycleDurationMs,
}: {
  count: number;
  active: number;
  onSelect: (i: number) => void;
  isPaused: boolean;
  cycleDurationMs: number;
}) => (
  <div className="flex items-center justify-center gap-4 mt-8">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        onClick={() => onSelect(i)}
        className="relative flex items-center justify-center w-6 h-6"
        aria-label={`Scenario ${i + 1}`}
      >
        <div
          className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
            i === active ? 'bg-accent' : 'bg-white/20 hover:bg-white/40'
          }`}
        />
        {i === active && !isPaused && (
          <motion.div
            key={`ring-${active}-${Date.now()}`}
            className="absolute inset-0 m-auto w-2.5 h-2.5 rounded-full border border-accent/40"
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 3, opacity: 0 }}
            transition={{ duration: cycleDurationMs / 1000, ease: 'linear' }}
          />
        )}
      </button>
    ))}
    {isPaused && (
      <span className="text-[10px] font-mono text-gray-600 ml-2 uppercase tracking-wider">paused</span>
    )}
  </div>
);

// ── Scenario Labels ──────────────────────────────────────

const ScenarioLabels = ({ scenarios, active, onSelect }: { scenarios: Scenario[]; active: number; onSelect: (i: number) => void }) => (
  <div className="flex items-center justify-center gap-1 mt-4 flex-wrap">
    {scenarios.map((s, i) => (
      <button
        key={s.id}
        onClick={() => onSelect(i)}
        className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-wider transition-colors ${
          i === active
            ? 'bg-accent/15 text-accent border border-accent/30'
            : 'text-gray-600 hover:text-gray-400'
        }`}
      >
        {s.title}
      </button>
    ))}
  </div>
);

// ── Main Component ───────────────────────────────────────

export const CommandCentreBase = ({ config }: { config: CommandCentreConfig }) => {
  const {
    scenarios,
    cycleDurationMs,
    productName,
    productLabel,
    heroTitle,
    heroSubtitle,
    backLabel = '← Kempion',
    backPath = '/',
    terminalLabel,
  } = config;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance timer
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % scenarios.length);
    }, cycleDurationMs);
    return () => clearInterval(timer);
  }, [isPaused, activeIndex, scenarios.length, cycleDurationMs]);

  const handleSelect = useCallback((i: number) => {
    setActiveIndex(i);
  }, []);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Link to={backPath} className="text-sm font-mono text-gray-500 hover:text-gray-300 transition-colors">
            {backLabel}
          </Link>
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-accent"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-xs font-mono text-accent/60 uppercase tracking-widest">{productLabel}</span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl md:text-4xl font-light text-white mb-3">{heroTitle}</h1>
          <p className="text-sm text-gray-500 max-w-lg mx-auto">
            {heroSubtitle}
          </p>
        </motion.div>

        {/* Scenario display */}
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait">
            <ScenarioPanel key={scenarios[activeIndex].id} scenario={scenarios[activeIndex]} />
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <ProgressDots
          count={scenarios.length}
          active={activeIndex}
          onSelect={handleSelect}
          isPaused={isPaused}
          cycleDurationMs={cycleDurationMs}
        />
        <ScenarioLabels scenarios={scenarios} active={activeIndex} onSelect={handleSelect} />

        {/* Terminal link */}
        {terminalLabel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-8"
          >
            <Link
              to={`/${productName.toLowerCase()}`}
              className="text-xs font-mono text-gray-600 hover:text-accent transition-colors border border-gray-800 hover:border-accent/30 px-4 py-2 rounded-lg"
            >
              {terminalLabel}
            </Link>
          </motion.div>
        )}
      </main>

      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[150px] opacity-30" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px] opacity-20" />
      </div>
    </div>
  );
};
